import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import ms from 'ms';

import { queryKeysAi } from '../queryKeys';

import { fetchProjectByModel } from './fetchProjectByModel';
import { type ProjectByModelResponse } from './types';

/**
 * Custom useQuery hook to fetch the project name for a given AI model.
 * @param modelId - The ID of the AI model to fetch the project name for.
 * @param options - Query options.
 * @returns The project name for the given AI model.
 * @see https://fa-vestigas-dev.azurewebsites.net/docs#/Metadata/get_project_by_model_get_project_by_model_get
 */
export const useQueryProjectByModel = <TData = ProjectByModelResponse>(
  modelId: string,
  options?: Omit<
    UseQueryOptions<ProjectByModelResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery<ProjectByModelResponse, Error, TData>({
    enabled: Boolean(modelId),
    queryFn: async ({ signal }) => fetchProjectByModel(modelId, { signal }),
    queryKey: queryKeysAi.getProjectByModel(modelId),
    staleTime: ms('24h'),
    ...options,
  });
